import { Fragment } from "react";
import { Menu, Transition } from "@headlessui/react";
import { EllipsisVerticalIcon } from "@heroicons/react/20/solid";

type MenuItem = "Edit" | "Repeat" | "Duplicate" | "Delete" | "Add to Program";

interface DropdownMenuProps extends CallbackProps {
  menuItems: MenuItem[];
  parentId: string;
}

type CallbackKey =
  | "onEditClicked"
  | "onRepeatClicked"
  | "onDuplicateClicked"
  | "onDeleteClicked"
  | "onAddToProgramClicked";

type CallbackProps = Partial<Record<CallbackKey, (id: string) => void>>;

const MENU_ITEM_CONFIG: Record<
  MenuItem,
  { handler: CallbackKey; label: string }
> = {
  Edit: { handler: "onEditClicked", label: "Edit" },
  Repeat: { handler: "onRepeatClicked", label: "Repeat" },
  Duplicate: { handler: "onDuplicateClicked", label: "Duplicate" },
  Delete: { handler: "onDeleteClicked", label: "Delete" },
  "Add to Program": {
    handler: "onAddToProgramClicked",
    label: "Add to Program",
  },
};

const DropdownMenu: React.FC<DropdownMenuProps> = ({
  menuItems,
  parentId,
  ...callbacks
}) => {
  return (
    <Menu as="div" className="relative">
      <Menu.Button className="pt-2 pr-1 pl-2 pb-1 block">
        <EllipsisVerticalIcon className="h-4 w-4" aria-hidden="true" />
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="py-1">
            {menuItems.map((item) => {
              const { handler, label } = MENU_ITEM_CONFIG[item];
              const callback = callbacks[handler] as
                | ((id: string) => void)
                | undefined;

              return (
                <Menu.Item key={item}>
                  {({ active }) => (
                    <button
                      className={`block px-4 py-2 text-sm w-full text-left ${
                        active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                      }`}
                      onClick={() => callback?.(parentId)}
                      type="button"
                    >
                      {label}
                    </button>
                  )}
                </Menu.Item>
              );
            })}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default DropdownMenu;
